import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import isEqual from 'lodash/isEqual';

import {
  cloneCharterFeederColumns,
  cloneCharterFeederRows,
  normalizeCharterFeederSheet,
} from './CharterFeederPricesHelpers';
import type { CharterFeederColumn, CharterFeederPersistedRow, CharterFeederSheetRow } from './CharterFeederPricesTypes';

interface UseCharterFeederSheetResult {
  rows: Array<CharterFeederSheetRow>;
  columns: Array<CharterFeederColumn>;
  originalRows: Array<CharterFeederPersistedRow>;
  initialRows: Array<CharterFeederSheetRow>;
  initialColumns: Array<CharterFeederColumn>;
  hasUnsavedChanges: boolean;
  setRows: React.Dispatch<React.SetStateAction<Array<CharterFeederSheetRow>>>;
  setColumns: React.Dispatch<React.SetStateAction<Array<CharterFeederColumn>>>;
  resetDraft: () => void;
  allowNextSync: () => void;
}

export function useCharterFeederSheet(
  persistedRows: Array<CharterFeederPersistedRow> | undefined,
): UseCharterFeederSheetResult {
  const { initialRows, initialColumns, originalRows } = useMemo(() => {
    const source = persistedRows ?? [];
    const normalized = normalizeCharterFeederSheet(source);
    return {
      originalRows: source.map((row) => ({ ...row })),
      initialRows: normalized.rows,
      initialColumns: normalized.columns,
    };
  }, [persistedRows]);

  const [rows, setRows] = useState<Array<CharterFeederSheetRow>>(() => cloneCharterFeederRows(initialRows));
  const [columns, setColumns] = useState<Array<CharterFeederColumn>>(() => cloneCharterFeederColumns(initialColumns));

  // Track the initialRows/initialColumns from the last sync so we can detect
  // whether local state diverged due to user edits (vs. just being stale from a
  // pending server fetch). On initial mount both refs start as the empty arrays
  // that rows/columns were initialised with.
  const lastSyncedRowsRef = useRef(initialRows);
  const lastSyncedColumnsRef = useRef(initialColumns);

  // When set to true the next server-data change will always sync regardless
  // of local edits. Used after a successful save so the grid reflects the
  // server response (which may contain real IDs for newly-created rows).
  const forceSyncRef = useRef(false);

  const allowNextSync = useCallback(() => {
    forceSyncRef.current = true;
  }, []);

  useEffect(() => {
    const prevRows = lastSyncedRowsRef.current;
    const prevColumns = lastSyncedColumnsRef.current;
    const forced = forceSyncRef.current;

    lastSyncedRowsRef.current = initialRows;
    lastSyncedColumnsRef.current = initialColumns;
    forceSyncRef.current = false;

    setRows((currentRows) => {
      if (forced || isEqual(currentRows, prevRows)) {
        return cloneCharterFeederRows(initialRows);
      }
      return currentRows;
    });

    setColumns((currentColumns) => {
      if (forced || isEqual(currentColumns, prevColumns)) {
        return cloneCharterFeederColumns(initialColumns);
      }
      return currentColumns;
    });
  }, [initialRows, initialColumns]);

  const hasUnsavedChanges = useMemo(
    () => !isEqual(rows, initialRows) || !isEqual(columns, initialColumns),
    [rows, initialRows, columns, initialColumns],
  );

  const resetDraft = useCallback(() => {
    lastSyncedRowsRef.current = initialRows;
    lastSyncedColumnsRef.current = initialColumns;
    setRows(cloneCharterFeederRows(initialRows));
    setColumns(cloneCharterFeederColumns(initialColumns));
  }, [initialRows, initialColumns]);

  return {
    rows,
    columns,
    originalRows,
    initialRows,
    initialColumns,
    hasUnsavedChanges,
    setRows,
    setColumns,
    resetDraft,
    allowNextSync,
  };
}
